import React, { FC, forwardRef, PropsWithChildren } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import useDeviceSize from '../../helpers/hooks/useDeviceSize';
import Slide from '@mui/material/Slide';
import { SlideProps } from '@mui/material/Slide/Slide';

const StyledInfoDialog = styled(Dialog)`
  .MuiPaper-root {
    max-width: 860px;
    max-height: 90vh;
    border-radius: 20px;

    .MuiDialogContent-root {
      padding: 0;
    }
  }
`;

const Content = styled(Box)`
  overflow: auto;
  flex: 1;
  padding: 9px 12px;
  border-radius: 4px;
  background-color: rgba(61, 177, 87, 0.10);
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 24px;
  top: 24px;
`;

interface InfoDialog {
  open: boolean;
  onClose: () => void;
  title: string;
  closeButtonTitle?: string;
  children: React.ReactNode;
}

const SlideUpAnimation = forwardRef(({ children, ...props }: SlideProps, ref) => {
  return <Slide direction="up" ref={ref} {...props}>{children}</Slide>;
});

const InfoDialog: FC<PropsWithChildren<InfoDialog>> = ({ open, onClose, title, children, closeButtonTitle }: InfoDialog) => {
  const { mobile, tablet } = useDeviceSize();

  return (
    <StyledInfoDialog
      fullScreen={mobile}
      open={open}
      onClose={onClose}
      aria-describedby="info-dialog-content"
      sx={{
        '.MuiPaper-root': {
          padding: mobile ? '40px' : tablet ? '24px' : '32px',
        },
      }}
      {...(mobile && { TransitionComponent: SlideUpAnimation })}
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <CloseButton onClick={onClose}>
          <CloseIcon/>
        </CloseButton>
        <Box id="info-dialog-content" marginBottom={mobile ? '32px' : '40px'} marginRight="40px">
          <Typography variant="h5" fontWeight={500}>{title}</Typography>
        </Box>
        <Content>
          {children}
        </Content>

        <Box marginTop={mobile ? '32px' : '40px'} display="flex" justifyContent="flex-end">
          <Button fullWidth={mobile} variant="contained" onClick={onClose}>{closeButtonTitle || 'Close'}</Button>
        </Box>
      </DialogContent>
    </StyledInfoDialog>
  );
};

export default InfoDialog;
